import { useRef, useState } from 'react';
import { useProspectingAvailability } from './useProspectingAvailability';
import { copyMessageLabel, copyPickerGroups, ordinaryCopyChoices } from './copyMessages';
import type { LeadActivity, LeadActivityKind } from './leadActivity';
import { Button, InlineError, Select } from '../../components/ui';
import { useCopySets, useLeadLists } from './hooks';
import { activityErrorMessage, useAttributeLeadActivity } from './leadActivity';
import { copySetForList, defaultChoice, type AttributionChoice } from './leadAttribution';

/**
 * "Which campaign was that?" — asked after the email is already logged.
 *
 * ── Why it comes after ─────────────────────────────────────────────────────
 *
 * Ten deals in the database, not one traceable to a campaign, because every
 * send before them was logged by hand and a hand-logged email had nowhere to
 * put a list. The obvious fix — two dropdowns on the Log email sent button —
 * defeats the button, which is one click precisely because it gets used most.
 *
 * Kyu picked "log it, then ask". The send records instantly, exactly as
 * before, and this appears underneath for the row just written. The fast path
 * stays fast and the campaign costs one optional click.
 *
 * ── Why "Not a campaign" is a button and not a silence ─────────────────────
 *
 * Walking away leaves the row blank, and so does pressing it — the database
 * ends up in the same state either way. The button exists so the person
 * knows blank was available and meant, rather than wondering whether the
 * prompt is waiting for something. Same reasoning as FollowUpPrompt's "Not
 * now": a prompt you cannot decline is a prompt people learn to fear.
 *
 * It does NOT punish dismissal, badge the row, or come back. An unattributed
 * email is a true statement about an email nobody could place.
 */
export function AttributionPrompt({
  activityId,
  leadId,
  choices,
  kind,
  activity,
  onDone,
}: {
  activityId: string;
  leadId: string;
  /** The lists this lead is actually on. Never rendered when empty. */
  choices: readonly AttributionChoice[];
  kind?: LeadActivityKind;
  activity?: LeadActivity | undefined;
  /** Called on success or dismissal — the caller closes it either way. */
  onDone: () => void;
}) {
  const { message_enabled } = useProspectingAvailability();
  const separateMessage = message_enabled && kind === 'linkedin';
  const versionedSession = useRef(separateMessage);
  if (separateMessage) versionedSession.current = true;
  const workflowPaused = versionedSession.current && !separateMessage;
  const lists = useLeadLists();
  const copySets = useCopySets();
  const attribute = useAttributeLeadActivity();
  const [error, setError] = useState('');
  // Preselected only when there is exactly one true answer; blank when there
  // are several, because the first one on screen would look like a fact.
  const [listId, setListId] = useState(defaultChoice(choices)?.listId ?? '');
  const [copySetId, setCopySetId] = useState(defaultChoice(choices)?.copySetId ?? '');

  const listNames = new Map((lists.data ?? []).map((list) => [list.id, list.name]));

  function save(nextList: string, nextCopy: string) {
    if (workflowPaused) return;
    setError('');
    if (separateMessage && activity === undefined) {
      setError('The logged DM is still loading. Try again shortly.');
      return;
    }
    attribute.mutate(
      {
        activityId,
        leadId,
        // Omitted, not empty: omitting clears the attribution, which is what
        // "Not a campaign" means and what a blank picker should do too.
        ...(nextList === '' ? {} : { listId: nextList }),
        ...(separateMessage && activity !== undefined
          ? { linkedinActivity: activity }
          : nextCopy === ''
            ? {}
            : { copySetId: nextCopy }),
      },
      {
        onSuccess: onDone,
        onError: (cause) => setError(activityErrorMessage(cause.message)),
      },
    );
  }

  return (
    <div className="lead-followup-prompt">
      {error !== '' && <InlineError>{error}</InlineError>}

      <p className="tracker-field-note">Logged. Which campaign was this?</p>

      <Select
        label="Campaign"
        value={listId}
        onChange={(event) => {
          const next = event.target.value;
          setListId(next);
          // Following the campaign is a convenience, not a claim: the copy set
          // on the membership is the best starting guess and stays editable,
          // because a list's copy changes over time and the one it carries now
          // is not necessarily the one that went out.
          setCopySetId(next === '' ? '' : (copySetForList(choices, next) ?? ''));
        }}
        options={[
          { value: '', label: 'Not a campaign' },
          ...choices.map((choice) => ({
            value: choice.listId,
            label: listNames.get(choice.listId) ?? 'Unnamed list',
          })),
        ]}
      />

      {!separateMessage && (
        <Select
          label="Copy used (optional)"
          value={copySetId}
          onChange={(event) => setCopySetId(event.target.value)}
          options={[
            { value: '', label: 'Not recorded' },
            ...(message_enabled
              ? ordinaryCopyChoices(copySets.data ?? [])
              : (copySets.data ?? [])
            ).map((set) => ({
              value: set.id,
              label: message_enabled ? copyMessageLabel(set) : set.name,
            })),
          ]}
          groups={message_enabled ? copyPickerGroups(copySets.data ?? [], false) : []}
        />
      )}

      <p className="tracker-field-note">
        Only lists this lead is on are offered — a campaign can only claim somebody who is on it.
        {separateMessage
          ? ' Message used is recorded separately. Not a campaign never clears it.'
          : ' The copy can be recorded on its own if you used a template from somewhere else.'}
      </p>

      <div className="lead-activity-actions">
        {workflowPaused && (
          <p role="status">This campaign edit is paused until availability returns.</p>
        )}
        <Button
          size="sm"
          disabled={workflowPaused || attribute.isPending}
          onClick={() => save(listId, copySetId)}
        >
          {attribute.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={workflowPaused || attribute.isPending}
          onClick={() => save('', '')}
        >
          Not a campaign
        </Button>
      </div>
    </div>
  );
}
